/**
 * ObservationTaskPanel — Main content panel for the CYP ASD Observation module.
 *
 * Renders the full observation form for whichever task the clinician has
 * selected from the sidebar. The form is divided into clearly headed sections
 * (no accordion / collapsible behaviour — all content is always visible):
 *
 *   Task Administration — materials used, setup details
 *   Criteria Contribution — table mapping this task's observations to the
 *       relevant ASD diagnostic criteria (DSM-5)
 *   Smart Notes — experimental rich note-taking surface with auto-detected
 *       direct-quote highlighting
 *
 * Each field is rendered according to its `type`:
 *   • radio    → row of selectable chip buttons (single-select)
 *   • checkbox → row of toggleable chip buttons (multi-select)
 *   • dropdown → styled <select> element
 *   • richtext → auto-resizing DictationTextArea
 *
 * All form state is managed locally through the `taskData` prop (lifted state
 * from the parent page component). Changes call `onFieldChange(fieldId, value)`
 * which the parent persists. For the initial mockup, no Firestore writes occur.
 */

import React, { useState, useCallback } from 'react';
import { ChevronDownIcon } from '@heroicons/react/solid';
import InfoTooltip from '../../../../components/InfoTooltip';
import DictationTextArea from '../../../../components/DictationTextArea';
import { DSM5_DOMAINS, DSM5_EVIDENCE_OPTIONS /* , CONTEXTUAL_FACTOR_OPTIONS */ } from '../config/observationTasksConfig';
import StructuredNotesEditor, { PROBING_Q_MIME } from './StructuredNotesEditor';
import { moveNoteBetweenArrays } from '../../../../utils/structuredNotesMove';
import { AUTISM_EVIDENCE_BUTTON_COLOURS } from '../../../../constants/autismEvidenceColors';

/** Evidence buttons — Clear = red, No evidence = green (shared Autism scale). */
const EVIDENCE_BUTTON_COLOURS = AUTISM_EVIDENCE_BUTTON_COLOURS;

export default function ObservationTaskPanel({
  task,
  taskData,
  onFieldChange,
  onDsmEvidenceSelected,
  selectedTaskIds = [],
  showObservations = false,
}) {
  const [removingCriterionId, setRemovingCriterionId] = useState(null);

  /** Controls whether the "Add Symptom" picker row is expanded to show buttons. */
  const [addSymptomPickerOpen, setAddSymptomPickerOpen] = useState(false);

  /** Section B (Observations) starts collapsed so the page isn't overwhelmingly long. */
  const [sectionBOpen, setSectionBOpen] = useState(false);

  const currentValue = (fieldId) => taskData?.[fieldId] ?? null;
  const currentOtherText = (fieldId) => taskData?.[`${fieldId}_otherText`] ?? '';

  /**
   * Moves a note card from one symptom row to another. Called by the
   * StructuredNotesEditor when a note is dropped onto a different row.
   * Both source and target arrays are updated via onFieldChange so the
   * parent persists the change.
   */
  const handleMoveNote = useCallback((sourceDomainId, noteId, targetDomainId, insertAt) => {
    const sourceKey = `dsm_${sourceDomainId}_notes`;
    const targetKey = `dsm_${targetDomainId}_notes`;
    const result = moveNoteBetweenArrays(taskData?.[sourceKey], taskData?.[targetKey], noteId, insertAt);
    if (!result) return;
    onFieldChange(sourceKey, result.source);
    onFieldChange(targetKey, result.target);
  }, [taskData, onFieldChange]);


  // -------------------------------------------------------------------
  // Field renderers
  // -------------------------------------------------------------------

  /**
   * Renders a single-select set of chip buttons. Selecting a new chip
   * deselects the previous one (radio behaviour). "Other (describe)"
   * is appended when the field has `hasOther: true` and reveals a text
   * input when selected.
   */
  const renderRadio = (field) => {
    const selected = currentValue(field.id);
    const allOptions = [...field.options, ...(field.hasOther ? ['Other (describe)'] : [])];
    const showConditional = field.conditional && selected === field.conditional.triggerValue;

    return (
      <div key={field.id}>
        <div className="flex items-start mb-4">
          <div className="w-72 flex-shrink-0 pt-1.5">
            <label className="text-slate-700 text-sm font-medium text-left">{field.label}</label>
          </div>
          <div className="flex-1 ml-4">
            <div className="flex flex-wrap gap-2 justify-start">
              {allOptions.map((option) => {
                const isSelected = selected === option;
                return (
                  <button
                    key={option}
                    onClick={() => onFieldChange(field.id, isSelected ? null : option)}
                    className={`
                      px-3 py-1.5 rounded-lg text-sm font-medium text-left
                      transition-all duration-150 border
                      ${isSelected
                        ? 'bg-primary/10 border-primary text-primary shadow-sm'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
                      }
                    `}
                    aria-pressed={isSelected}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
            {field.hasOther && selected === 'Other (describe)' && (
              <input
                type="text"
                value={currentOtherText(field.id)}
                onChange={(e) => onFieldChange(`${field.id}_otherText`, e.target.value)}
                placeholder="Please describe…"
                className="mt-2 w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm text-left focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40"
                aria-label={`${field.label} — other description`}
              />
            )}
          </div>
        </div>
        {/* Conditional follow-up — renders as its own full-width row so it
            aligns with the parent field rather than appearing indented. */}
        {field.conditional && (
          <div
            className="overflow-hidden transition-all duration-200 ease-in-out"
            style={{
              display: 'grid',
              gridTemplateRows: showConditional ? '1fr' : '0fr',
              opacity: showConditional ? 1 : 0,
            }}
          >
            <div className="min-h-0">
              {renderField(field.conditional.field)}
            </div>
          </div>
        )}
      </div>
    );
  };

  /**
   * Renders a multi-select set of chip buttons. Multiple chips can be
   * toggled on simultaneously. Uses a subtle colour differentiation from
   * single-select to help clinicians distinguish the interaction model.
   */
  const renderCheckbox = (field) => {
    const selected = currentValue(field.id) || [];
    const allOptions = [...field.options, ...(field.hasOther ? ['Other (describe)'] : [])];

    const toggle = (option) => {
      const next = selected.includes(option)
        ? selected.filter(o => o !== option)
        : [...selected, option];
      onFieldChange(field.id, next);
    };

    return (
      <div className="flex items-start mb-4" key={field.id}>
        <div className="w-72 flex-shrink-0 pt-1.5">
          <label className="text-slate-700 text-sm font-medium text-left">
            {field.label}
            <span className="text-gray-400 text-xs ml-1 block text-left">(select all that apply)</span>
          </label>
        </div>
        <div className="flex-1 ml-4">
          <div className="flex flex-wrap gap-2 justify-start">
            {allOptions.map((option) => {
              const isSelected = selected.includes(option);
              return (
                <button
                  key={option}
                  onClick={() => toggle(option)}
                  className={`
                    px-3 py-1.5 rounded-lg text-sm font-medium text-left
                    transition-all duration-150 border
                    ${isSelected
                      ? 'bg-tertiary border-primary/30 text-gray-800 shadow-sm'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
                    }
                  `}
                  aria-pressed={isSelected}
                >
                  {isSelected && <span className="mr-1">✓</span>}
                  {option}
                </button>
              );
            })}
          </div>
          {field.hasOther && selected.includes('Other (describe)') && (
            <input
              type="text"
              value={currentOtherText(field.id)}
              onChange={(e) => onFieldChange(`${field.id}_otherText`, e.target.value)}
              placeholder="Please describe…"
              className="mt-2 w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm text-left focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40"
              aria-label={`${field.label} — other description`}
            />
          )}
        </div>
      </div>
    );
  };

  /**
   * Renders a single-select set of chip buttons — identical to renderRadio.
   * Previously a <select> dropdown, now converted to buttons for design
   * parity across the module. "Other" reveals a text input when selected.
   */
  const renderDropdown = (field) => {
    const selected = currentValue(field.id) || '';
    const allOptions = [...field.options, ...(field.hasOther ? ['Other (describe)'] : [])];

    return (
      <div className="flex items-start mb-4" key={field.id}>
        <div className="w-72 flex-shrink-0 pt-1.5">
          <label className="text-slate-700 text-sm font-medium text-left">{field.label}</label>
        </div>
        <div className="flex-1 ml-4">
          <div className="flex flex-wrap gap-2 justify-start">
            {allOptions.map((option) => {
              const optValue = option === 'Other (describe)' ? '__other__' : option;
              const isSelected = selected === optValue;
              return (
                <button
                  key={option}
                  onClick={() => onFieldChange(field.id, isSelected ? '' : optValue)}
                  className={`
                    px-3 py-1.5 rounded-lg text-sm font-medium text-left
                    transition-all duration-150 border
                    ${isSelected
                      ? 'bg-primary/10 border-primary text-primary shadow-sm'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
                    }
                  `}
                  aria-pressed={isSelected}
                >
                  {option}
                </button>
              );
            })}
          </div>
          {field.hasOther && selected === '__other__' && (
            <input
              type="text"
              value={currentOtherText(field.id)}
              onChange={(e) => onFieldChange(`${field.id}_otherText`, e.target.value)}
              placeholder="Please describe…"
              className="mt-2 w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm text-left focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40"
              aria-label={`${field.label} — other description`}
            />
          )}
        </div>
      </div>
    );
  };

  /** Renders an auto-resizing rich text area for clinical narrative */
  const renderRichText = (field) => {
    const value = currentValue(field.id) || '';
    return (
      <div className="mb-4" key={field.id}>
        <label className="text-slate-700 text-sm font-semibold block mb-2 text-left">{field.label}</label>
        <DictationTextArea
          value={value}
          onChange={(val) => onFieldChange(field.id, val)}
          canEdit={true}
          ariaLabel={field.label}
          minHeight="8rem"
          placeholder="Enter clinical notes…"
        />
      </div>
    );
  };

  /**
   * Renders an ordered list of clinician-administered questions with
   * "mark as asked" toggle — mirrors the SSI probing-question UX.
   *
   * Clicking a question toggles it between asked (blue highlight with
   * "Marked as asked" label) and un-asked (neutral). The list of asked
   * question indices is persisted in taskData so it survives task switching.
   */
  const renderQuestionnaire = (field) => {
    const asked = currentValue(field.id) || [];
    const askedSet = new Set(asked);

    const toggleAsked = (idx) => {
      const next = askedSet.has(idx)
        ? asked.filter(i => i !== idx)
        : [...asked, idx];
      onFieldChange(field.id, next);
    };

    return (
      <div className="mb-4" key={field.id}>
        <label className="text-slate-700 text-sm font-semibold block mb-3 text-left">{field.label}</label>
        <div className="flex flex-col gap-1.5">
          {field.questions.map((q, idx) => {
            const isAsked = askedSet.has(idx);
            return (
              <div
                key={idx}
                draggable="true"
                onDragStart={(e) => {
                  e.dataTransfer.setData(PROBING_Q_MIME, q);
                  e.dataTransfer.setData('text/plain', q);
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                onClick={() => toggleAsked(idx)}
                className={`
                  px-2 py-1.5 rounded-lg text-xs font-medium italic leading-relaxed
                  cursor-pointer select-none transition-all duration-200 border
                  ${isAsked
                    ? 'bg-blue-50 text-blue-700 border-blue-200'
                    : 'bg-white text-gray-800 border-gray-100 hover:bg-blue-50/60 hover:border-blue-100'}
                `}
                title={isAsked ? 'Click to unmark — or drag into notes' : 'Click to mark as asked — or drag into notes'}
              >
                <div className="flex items-start gap-2.5">
                  <span className="text-sm leading-relaxed flex-shrink-0 text-gray-400">•</span>
                  <span className="flex-1">&ldquo;{q}&rdquo;</span>
                  {isAsked && (
                    <span className="text-[10px] font-semibold text-blue-500 not-italic flex-shrink-0 whitespace-nowrap">
                      Marked as asked
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /** Routes a field to the appropriate renderer based on its type */
  const renderField = (field) => {
    switch (field.type) {
      case 'radio': return renderRadio(field);
      case 'checkbox': return renderCheckbox(field);
      case 'dropdown': return renderDropdown(field);
      case 'richtext': return renderRichText(field);
      case 'questionnaire': return renderQuestionnaire(field);
      default: return null;
    }
  };

  // -------------------------------------------------------------------
  // Section renderers
  // -------------------------------------------------------------------

  /**
   * Simple section heading — no accordion, no badge. Just a title with
   * an optional info tooltip displayed inline.
   */
  const renderSectionHeading = (title, tooltip) => (
    <div className="flex items-center gap-2 mb-3">
      <h2 className="text-gray-900 text-base font-semibold text-left">{title}</h2>
      {tooltip && (
        <InfoTooltip content={tooltip} label={`${title} info`} maxWidth="320px" />
      )}
    </div>
  );

  /*
   * Section C — Contextual Factors
   * Hidden while the team focuses on the DSM-5 criteria mapping design.
   * To restore, uncomment this function and its call site in the render.
   *
   * const renderContextualFactors = () => {
   *   const selected = currentValue('contextualFactors') || [];
   *   const sectionId = 'C';
   *   const expanded = expandedSections[sectionId];
   *   const toggle = (optionId) => {
   *     let next;
   *     if (optionId === 'none') {
   *       next = selected.includes('none') ? [] : ['none'];
   *     } else {
   *       next = selected.includes(optionId)
   *         ? selected.filter(o => o !== optionId)
   *         : [...selected.filter(o => o !== 'none'), optionId];
   *     }
   *     onFieldChange('contextualFactors', next);
   *   };
   *   return (
   *     <div>
   *       {renderSectionHeader(sectionId, 'Section C — Contextual Factors', expanded)}
   *       {expanded && (
   *         <div className="mt-3 px-5 py-4 bg-white rounded-xl border border-gray-100 max-h-[32rem] overflow-y-auto">
   *           ... contextual factors checklist ...
   *         </div>
   *       )}
   *     </div>
   *   );
   * };
   */

  /**
   * Section D — Criteria Contribution
   *
   * Starts with the criteria this task is designed to elicit evidence for
   * (from task.relevantCriteria). Clinicians can add extra criteria via
   * the "+" row at the bottom — useful when unexpected behaviours surface.
   *
   * Each criterion row contains:
   *   • Colour-coded badge (A = blue, B = orange)
   *   • Truncated title with full-text tooltip
   *   • Evidence dropdown (Observed / Not observed / Inconclusive / …)
   *   • Free-text notes input for detail on what was observed
   *   • Remove button for clinician-added extras
   *
   * Extra criteria IDs are persisted in taskData.dsm_extraCriteria so they
   * survive task-switching without data loss.
   */
  const renderDsmDomains = () => {
    // Merge the task's default criteria with any extras the clinician added
    const extraCriteria = taskData?.dsm_extraCriteria || [];
    const activeCriteriaIds = [
      ...(task.relevantCriteria || []),
      ...extraCriteria.filter(id => !(task.relevantCriteria || []).includes(id)),
    ];
    const activeDomains = activeCriteriaIds
      .map(id => DSM5_DOMAINS.find(d => d.id === id))
      .filter(Boolean);

    // Criteria not yet active — available for the "+" row
    const remainingDomains = DSM5_DOMAINS.filter(d => !activeCriteriaIds.includes(d.id));

    const handleAddCriterion = (criterionId) => {
      if (!criterionId) return;
      onFieldChange('dsm_extraCriteria', [...extraCriteria, criterionId]);
    };

    // Animated removal: set the "removing" flag to trigger exit animation,
    // then actually remove from state after the animation duration elapses.
    const handleRemoveExtraCriterion = (criterionId) => {
      setRemovingCriterionId(criterionId);
      setTimeout(() => {
        onFieldChange('dsm_extraCriteria', extraCriteria.filter(id => id !== criterionId));
        onFieldChange(`dsm_${criterionId}_evidence`, null);
        onFieldChange(`dsm_${criterionId}_notes`, '');
        setRemovingCriterionId(null);
      }, 200);
    };

    const sectionTooltip =
      `Default criteria for this task: ${task.relevantCriteria?.join(', ')}. ` +
      'Use the + row below to add additional criteria if needed. ' +
      'Final clinical interpretation requires synthesis across the full assessment.';

    const rowColor = (val) => {
      switch (val) {
        case 'No evidence':      return 'bg-emerald-50';
        case 'Partial evidence': return 'bg-amber-50';
        case 'Clear evidence':   return 'bg-red-50';
        case 'Inconclusive':     return 'bg-gray-100';
        default:                 return 'bg-gray-50';
      }
    };

    return (
      <div className="mb-6">
        {renderSectionHeading('DSM Criteria Contribution', sectionTooltip)}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <table className="w-full border-collapse text-left">
              {/* Column header row — matches the SSI Review table style */}
              <thead>
                <tr className="bg-primary text-white">
                  <th className="px-4 py-3 text-sm font-bold text-left" style={{ width: '30%' }}>DSM Criteria</th>
                  <th className="px-4 py-3 text-sm font-bold text-left" style={{ width: '140px' }}>Threshold met</th>
                  <th className="px-4 py-3 text-sm font-bold text-left">Notes</th>
                  <th className="w-10" />
                </tr>
              </thead>

              <tbody>
                {activeDomains.map((domain) => {
                  const isExtra = extraCriteria.includes(domain.id);
                  const evidenceValue = currentValue(`dsm_${domain.id}_evidence`) || '';
                  const notesValue = currentValue(`dsm_${domain.id}_notes`) || '';
                  const isRemoving = removingCriterionId === domain.id;

                  return (
                    <tr
                      key={domain.id}
                      className={`border-b border-gray-200 last:border-b-0 transition-colors duration-300 ${rowColor(evidenceValue)}`}
                      style={
                        isRemoving
                          ? { opacity: 0, maxHeight: 0, transition: 'all 200ms ease-in' }
                          : { animation: 'criterionSlideIn 200ms ease-out' }
                      }
                    >
                      {/* Symptom — short title heading + full description subtitle */}
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-start gap-1.5">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900 font-bold leading-snug">
                              {domain.id}. {domain.shortTitle}
                            </p>
                            <p className="text-xs text-gray-500 leading-snug mt-0.5">
                              {domain.title}
                            </p>
                          </div>
                          <InfoTooltip
                            content={domain.tooltip}
                            label={`${domain.id} criteria detail`}
                            maxWidth="300px"
                          />
                        </div>
                      </td>

                      {/* Symptom Present — colour-coded buttons matching the SSI */}
                      <td className="px-4 py-3 align-top">
                        <div className="flex flex-col gap-1.5">
                          {DSM5_EVIDENCE_OPTIONS.map(opt => {
                            const isSelected = evidenceValue === opt;
                            const colours = EVIDENCE_BUTTON_COLOURS[opt] || EVIDENCE_BUTTON_COLOURS['Inconclusive'];
                            return (
                              <button
                                key={opt}
                                onClick={() => {
                                  const newValue = isSelected ? null : opt;
                                  onFieldChange(`dsm_${domain.id}_evidence`, newValue);
                                  if (newValue && onDsmEvidenceSelected) {
                                    onDsmEvidenceSelected(domain.id, newValue);
                                  }
                                }}
                                className={`
                                  px-2.5 py-1 rounded-lg text-xs font-semibold border
                                  transition-all duration-150 whitespace-nowrap
                                  ${isSelected
                                    ? `${colours.selectedBg} ${colours.selectedBorder} text-white shadow-sm`
                                    : `bg-white text-gray-600 border-gray-300 ${colours.hoverBorder}`
                                  }
                                `}
                                aria-pressed={isSelected}
                                aria-label={`${opt} for ${domain.id}`}
                              >
                                {opt}
                              </button>
                            );
                          })}
                        </div>
                      </td>

                      {/* Notes — structured note cards with quote detection and DnD */}
                      <td className="px-4 py-3 align-top">
                        <StructuredNotesEditor
                          notes={notesValue}
                          onChange={(val) => onFieldChange(`dsm_${domain.id}_notes`, val)}
                          placeholder={`Notes for ${domain.id}…`}
                          minHeight="7.5rem"
                          domainId={domain.id}
                          onMoveNoteIn={handleMoveNote}
                        />
                      </td>

                      {/* Remove button (only for clinician-added extras) */}
                      <td className="px-2 py-3 align-top">
                        {isExtra ? (
                          <button
                            onClick={() => handleRemoveExtraCriterion(domain.id)}
                            className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                            title="Remove this criterion"
                            aria-label={`Remove ${domain.id}`}
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        ) : (
                          <span className="block w-4" />
                        )}
                      </td>
                    </tr>
                  );
                })}

                {/* "+ Add Symptom" row — expands to show remaining symptom buttons */}
                {remainingDomains.length > 0 && (
                  <tr className="border-t border-dashed border-gray-300">
                    <td colSpan={4} className="px-4 py-2.5">
                      {!addSymptomPickerOpen ? (
                        <button
                          onClick={() => setAddSymptomPickerOpen(true)}
                          className="
                            flex items-center gap-2 text-sm font-semibold text-gray-500
                            hover:text-primary transition-colors duration-150
                          "
                        >
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-gray-100 text-gray-400 text-xs font-bold flex-shrink-0 group-hover:bg-primary/10 group-hover:text-primary">
                            +
                          </span>
                          Add DSM Criteria
                        </button>
                      ) : (
                        <div
                          className="flex flex-wrap items-center gap-2"
                          style={{ animation: 'criterionSlideIn 200ms ease-out' }}
                        >
                          <span className="text-xs text-gray-500 font-medium mr-1">Add:</span>
                          {remainingDomains.map(d => (
                            <button
                              key={d.id}
                              onClick={() => {
                                handleAddCriterion(d.id);
                                setAddSymptomPickerOpen(false);
                              }}
                              className="
                                px-2.5 py-1 rounded-lg text-xs font-semibold
                                border border-gray-300 bg-white text-gray-600
                                hover:border-primary hover:text-primary hover:bg-primary/5
                                transition-all duration-150
                              "
                            >
                              {d.id}. {d.shortTitle}
                            </button>
                          ))}
                          <button
                            onClick={() => setAddSymptomPickerOpen(false)}
                            className="ml-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                            title="Cancel"
                          >
                            ✕
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
      </div>
    );
  };

  // -------------------------------------------------------------------
  // Main render
  // -------------------------------------------------------------------

  return (
    <div className="flex-grow p-6 text-left">
      {/* Keyframes for criterion row entrance animation */}
      <style>{`
        @keyframes criterionSlideIn {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      {/* Task header — mirrors the SSI criterion panel heading style */}
      <div className="mb-6 text-left">
        <h1 className="text-gray-900 text-3xl font-bold">
          Task {selectedTaskIds.indexOf(task.id) + 1}. {task.shortTitle}
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          <span className="font-semibold">Focus of observation: </span>
          {task.focusOfObservation || task.fullTitle}
        </p>
      </div>

      {/* Section A — Task Administration (read-only reference info) */}
      {task.sections.filter(s => s.id === 'A').map((section) => {
        const questionnaireFields = (section.fields || []).filter(f => f.type === 'questionnaire');
        const hasQuestions = questionnaireFields.length > 0;

        return (
          <div key={section.id} className="mb-6">
            {renderSectionHeading(section.title)}
            <div className="px-5 py-4 bg-white rounded-xl border border-gray-100">
              {/* External resource cards — thumbnail + label, each opens in a new tab */}
              {task.externalResources?.length > 0 && (
                <div className="flex gap-3 mb-4 flex-wrap">
                  {task.externalResources.map((res, idx) => (
                    <a
                      key={idx}
                      href={res.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex flex-col items-center w-32 rounded-xl
                        border border-gray-200 bg-gray-50
                        hover:border-primary/40 hover:shadow-md
                        transition-all duration-200 overflow-hidden"
                    >
                      <div className="w-full h-20 overflow-hidden bg-gray-100">
                        <img
                          src={res.thumbnail}
                          alt={res.label}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="flex items-center gap-1.5 px-2 py-2 w-full">
                        <span className="text-xs font-semibold text-gray-700 group-hover:text-primary truncate transition-colors">
                          {res.label}
                        </span>
                        <svg className="w-3 h-3 flex-shrink-0 text-gray-300 group-hover:text-primary/60 transition-colors ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </div>
                    </a>
                  ))}
                </div>
              )}

              {hasQuestions ? (
                // Top-level topic groups get a generous gap; nested branches
                // (field.nested) sit tighter under their parent as quieter
                // sub-subheadings.
                <div className="flex flex-col">
                {questionnaireFields.map((field, fieldIndex) => {
                  const asked = currentValue(field.id) || [];
                  const askedSet = new Set(asked);
                  const isNested = Boolean(field.nested);

                  const toggleAsked = (idx) => {
                    const next = askedSet.has(idx)
                      ? asked.filter(i => i !== idx)
                      : [...asked, idx];
                    onFieldChange(field.id, next);
                  };

                  return (
                    <div
                      key={field.id}
                      className={fieldIndex === 0 ? '' : isNested ? 'mt-2.5' : 'mt-4'}
                    >
                      <label
                        className={`block text-left ${
                          isNested
                            ? 'text-slate-500 text-xs font-medium mb-1.5 pl-0.5'
                            : 'text-slate-700 text-sm font-semibold mb-2'
                        }`}
                      >
                        {typeof field.label === 'string' && field.label.includes('__')
                          ? field.label.split(/(__[^_]+__)/).map((part, i) =>
                              part.startsWith('__') && part.endsWith('__')
                                ? <u key={i}>{part.slice(2, -2)}</u>
                                : part
                            )
                          : field.label}
                      </label>
                      <div className={`flex flex-col gap-1.5 ${isNested ? 'pl-0.5' : ''}`}>
                        {field.questions.map((q, idx) => {
                          const isAsked = askedSet.has(idx);
                          return (
                            <div
                              key={idx}
                              draggable="true"
                              onDragStart={(e) => {
                                e.dataTransfer.setData(PROBING_Q_MIME, q);
                                e.dataTransfer.setData('text/plain', q);
                                e.dataTransfer.effectAllowed = 'copy';
                              }}
                              onClick={() => toggleAsked(idx)}
                              className={`
                                px-2 py-1.5 rounded-lg text-xs font-medium italic leading-relaxed
                                cursor-pointer select-none transition-all duration-200 border
                                ${isAsked
                                  ? 'bg-blue-50 text-blue-700 border-blue-200'
                                  : 'bg-white text-gray-800 border-gray-100 hover:bg-blue-50/60 hover:border-blue-100'}
                              `}
                              title={isAsked ? 'Click to unmark — or drag into notes' : 'Click to mark as asked — or drag into notes'}
                            >
                              {/* items-start keeps the step number on the first line when copy wraps */}
                              <div className="flex items-start gap-2">
                                <span className="text-gray-400 not-italic font-semibold flex-shrink-0 min-w-[1.25rem] text-right">{idx + 1}.</span>
                                <span className="flex-1">{q}</span>
                                {isAsked && (
                                  <span className="text-[10px] font-semibold text-blue-500 not-italic flex-shrink-0 whitespace-nowrap">
                                    Marked as asked
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                </div>
              ) : !task.externalResources?.length && (
                <div className="flex items-center gap-2 py-3 px-4 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                  <svg className="w-5 h-5 text-gray-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm text-gray-400 italic">Helpful information to be displayed here</span>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Section B — Observations (collapsible per-task behavioural checkboxes).
         Only rendered when the global "Observations" toggle is on. */}
      {showObservations && task.sections.filter(s => s.id === 'B').map((section) => (
        <div key={section.id} className="mb-6">
          <button
            type="button"
            onClick={() => setSectionBOpen(prev => !prev)}
            className="flex items-center gap-2 mb-3 group cursor-pointer"
          >
            <h2 className="text-gray-900 text-base font-semibold text-left">{section.title}</h2>
            <ChevronDownIcon
              className={`w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-transform duration-200 ${sectionBOpen ? 'rotate-0' : '-rotate-90'}`}
            />
          </button>
          <div
            className="overflow-hidden transition-all duration-300 ease-in-out"
            style={{
              display: 'grid',
              gridTemplateRows: sectionBOpen ? '1fr' : '0fr',
              opacity: sectionBOpen ? 1 : 0,
            }}
          >
            <div className="min-h-0">
              <div className="px-5 py-4 bg-white rounded-xl border border-gray-100 space-y-5">
                {section.subsections?.map((sub, idx) => (
                  <div key={idx}>
                    <h3 className="text-sm font-semibold text-gray-800 mb-2 text-left">{sub.title}</h3>
                    {sub.fields.map(f => renderField(f))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Criteria Contribution — DSM-5 domain mapping for this task */}
      {renderDsmDomains()}

    </div>
  );
}
